import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { usePlans } from "@/hooks/use-plans";
import { usePromoCode, getPromoWithNearestExpiry } from "@/hooks/use-promo-code";
import Countdown from "@/components/common/Countdown";
import { formatDiscount } from "@/lib/utils";
import { track, EVENTS } from "@/lib/mixpanel";
import { getStripeLookupKey } from "@/lib/checkout";
import { shouldShowCountdown } from "@/lib/countdown";
import { getDecimalCount } from "@/lib/currencies";

interface DesktopPurchaseStickyBannerProps {
  isVisible: boolean;
  onPurchase: (planId: string) => void;
  onScrollToPlans: () => void;
}

const DesktopPurchaseStickyBanner = ({ isVisible, onPurchase, onScrollToPlans }: DesktopPurchaseStickyBannerProps) => {
  const { t, i18n } = useTranslation();
  const { plans, isLoading } = usePlans();
  const { eventCode, eventData, referralCode, referralData } = usePromoCode();

  const annualPlan = plans.find(plan => plan.id === 'annual');
  const trialPeriodDays = annualPlan?.pricing.trialPeriodDays;

  const countdownData = getPromoWithNearestExpiry(eventData, referralData);
  const countdownTargetDate = countdownData?.expires_at
    ? new Date(countdownData.expires_at * 1000)
    : null;

  if (!annualPlan) return null;

  return (
    <div
      className={`
        hidden lg:flex fixed top-0 right-0 z-20 w-full pointer-events-none
        transition-all duration-500 ease-in-out transform
        ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
      `}
    >
      <div className="container grid lg:grid-cols-12 lg:gap-0 xl:gap-8 pt-6 mx-auto pointer-events-auto">
        <div className="lg:col-span-8 xl:col-span-7 lg:col-start-0 xl:col-start-2"></div>
        <div className="lg:col-span-4 pr-4 pl-6">
          <div className="px-0 pt-0 pb-4 flex flex-col justify-center gap-4 bg-white rounded-xl overflow-hidden border-slate-200 border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)]">
            {countdownTargetDate && countdownData?.valid && shouldShowCountdown(countdownTargetDate) && (
              <div className="w-full h-9">
                <div className="bg-slate-200 px-4 h-full flex items-center ">
                  <Countdown
                    targetDate={countdownTargetDate}
                    className="mx-auto"
                    textColor="text-slate-700"
                    numberColor="text-red-600"
                    promoCode={eventCode || referralCode}
                  />
                </div>
              </div>
            )}

            <div className="w-full flex flex-col justify-center gap-4 flex-grow px-4 mt-4">
              <div className="flex gap-4 items-center">

                <div className="relative w-36 h-36 rounded-lg overflow-hidden">
                  <img src='https://cdn-site.clarymind.com/product-page-app/plans/pro-annual-product-image-v2.jpg' alt="Annual Plan Preview" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col items-start gap-1">

                  <p className="text-xs text-red-500 font-medium">
                    {t(annualPlan?.pricing.discount?.label || '') + ' ' + formatDiscount(annualPlan?.pricing.discount?.percentage || 0, i18n.language)}
                  </p>

                  {annualPlan?.pricing.current && (
                    <div className="flex flex-row items-baseline justify-center gap-2">
                      <span className="text-2xl font-semibold text-red-500">
                        {annualPlan?.pricing.currency} {Number(annualPlan?.pricing.current).toLocaleString()}
                      </span>

                      <span className={`text-sm text-red-500`}> / {t(annualPlan.unit)}</span>
                    </div>
                  )}

                  {annualPlan?.pricing.original && (
                    <div className="flex flex-col items-start gap-1.5">

                      <div className="text-xs text-slate-600">
                        ({annualPlan.pricing.currency} {(annualPlan.pricing.current / annualPlan.duration.months).toFixed(getDecimalCount(annualPlan.pricing.currency)).toLocaleString()} / {t('purchase.perMonth')})
                      </div>

                      <div className="flex flex-row items-baseline justify-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400 line-through">
                          {annualPlan?.pricing.currency} {Number(annualPlan?.pricing.original).toLocaleString()}
                          {/* <span className={`text-xs text-slate-400`}> / {t(annualPlan.unit)}</span> */}
                        </span>
                      </div>
                    </div>
                  )}

                </div>
              </div>

              <div className="h-[1px] w-full bg-slate-200/50 mt-1 mx-0.5"></div>

              <div className="flex flex-col w-full gap-1 flex-grow mt-1">
                <Button
                  className="h-full bg-yellow-300 hover:bg-yellow-400 px-8 py-3 text-base text-slate-800 border-b-4 border-yellow-400 rounded-lg font-bold"
                  onClick={() => {
                    track(EVENTS.INITIATE_PURCHASE, {
                      planId: 'annual',
                      planPrice: annualPlan?.pricing.current,
                      currency: annualPlan?.pricing.currency,
                      promoCode: referralCode ?? undefined,
                      stripeLookupKey: getStripeLookupKey('annual')
                    });
                    onPurchase('annual');
                  }}
                >
                  {trialPeriodDays
                    ? t('purchase.cta.startTrial', { days: trialPeriodDays })
                    : t('purchase.cta.subscribe')}
                </Button>
                <Button
                  variant="link"
                  className="text-sm text-slate-400 py-1 h-8 underline w-full"
                  onClick={() => {
                    onScrollToPlans();
                  }}
                >
                  {t('purchase.cta.viewAllPlans')}
                </Button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopPurchaseStickyBanner;
